<template lang="pug">
b-tabs(v-if="displayBookings" v-model="activeTab" v-on:change="onChange")
  b-tab-item(icon="plus")
    component(v-bind:is="component" v-bind:initialBookingStartDate="bookingStartDate")
  template(v-if="isClient()")
    b-tab-item(label="Search by Gym" v-if="isOperatorWithGyms")
      client-professionals(v-bind:mode="SEARCH_BY_GYM")
    b-tab-item(label="Search by Online Gym" v-if="isOperatorWithOnlineGyms")
      client-professionals(v-bind:mode="SEARCH_BY_GYM_ONLINE")
    b-tab-item(label="Search by Location" v-if="isOperatorWithLocations")
      client-professionals(v-bind:mode="SEARCH_BY_LOCATION")
  b-tab-item(label="Requested" v-if="isProfessional()")
    refresh(v-bind:impl='refreshImpl')
    booking(v-bind:booking="booking" v-bind:key="booking.id" v-for="booking in bookingsRequested")
    comment(v-if="noRequestedBookingsFound" comment="No requested bookings found.")
  b-tab-item(label="Upcoming")
    refresh(v-bind:impl='refreshImpl')
    booking(v-bind:booking="booking" v-bind:key="booking.id" v-for="booking in bookingsUpcoming")
    comment(v-if="noUpcomingBookingsFound" comment="No upcoming bookings found.")
  b-tab-item(label="Past")
    refresh(v-bind:impl='refreshImpl')
    booking(v-bind:booking="booking" v-bind:key="booking.id" v-for="booking in bookingsPast")
    comment(v-if="noPastBookingsFound" comment="No past bookings found.")
  b-tab-item(label="Rejected")
    refresh(v-bind:impl='refreshImpl')
    booking(v-bind:booking="booking" v-bind:key="booking.id" v-for="booking in bookingsRejected")
    comment(v-if="noRejectedBookingsFound" comment="No rejected bookings found.")
  b-tab-item(label="Cancelled")
    refresh(v-bind:impl='refreshImpl')
    booking(v-bind:booking="booking" v-bind:key="booking.id" v-for="booking in bookingsCancelled")
    comment(v-if="noCancelledBookingsFound" comment="No cancelled bookings found.")
</template>

<script>
import { parse } from 'date-fns'

import Constants from '@/constants'

import AddClientBooking from '@/components/AddClientBooking.vue'
import AddProfessionalBooking from '@/components/AddProfessionalBooking.vue'
import Booking from '@/components/Booking.vue'
import ClientProfessionals from '@/components/ClientProfessionals.vue'
import Comment from '@/components/Comment.vue'
import Refresh from '@/components/Refresh.vue'

import ApiMixin from '@/mixins/ApiMixin.vue'

export default {
  components: {
    AddClientBooking,
    AddProfessionalBooking,
    Booking,
    ClientProfessionals,
    Comment,
    Refresh
  },
  mixins: [
    ApiMixin
  ],
  data () {
    return {
      activeTab: null,
      bookingStartDate: this.$route.params.date || null,
      locationId: 0,
      now: null,
      professionalId: this.$route.params.professional || 0
    }
  },
  computed: {
    bookings () {
      return this.isClient() ? this.apiClientBookings : this.apiProfessionalBookings
    },
    bookingsCancelled () {
      return this.bookings.filter((booking) => [Constants.STATUS_CANCELLED].includes(booking.booking_status))
    },
    bookingsPast () {
      return this.bookings.filter((booking) => [Constants.STATUS_ACCEPTED, Constants.STATUS_COMPLETED, Constants.STATUS_REQUESTED, Constants.STATUS_UNCOMPLETED_CLIENT, Constants.STATUS_UNCOMPLETED_PROFESSIONAL].includes(booking.booking_status) && this.parsedDateTime(booking.booking_start) <= this.now)
    },
    bookingsRejected () {
      return this.bookings.filter((booking) => [Constants.STATUS_REJECTED].includes(booking.booking_status))
    },
    bookingsRequested () {
      return this.bookings.filter((booking) => [Constants.STATUS_REQUESTED].includes(booking.booking_status) && this.parsedDateTime(booking.booking_start) > this.now)
    },
    bookingsUpcoming () {
      return this.bookings.filter((booking) => [Constants.STATUS_ACCEPTED, Constants.STATUS_REQUESTED].includes(booking.booking_status) && this.parsedDateTime(booking.booking_start) > this.now)
    },
    component () {
      return this.isProfessional() ? 'add-professional-booking' : 'add-client-booking'
    },
    defaultTab () {
      return this.$route.params.professional ? 0 : 1
    },
    displayBookings () {
      if (this.isClient() && this.apiClient && this.apiClient.status === Constants.STATUS_VERIFIED) {
        return true
      }
      if (this.isProfessional() && this.apiProfessional && this.apiProfessional.status === Constants.STATUS_VERIFIED) {
        return true
      }
      return false
    },
    noCancelledBookingsFound () {
      return this.bookingsCancelled.length === 0
    },
    noPastBookingsFound () {
      return this.bookingsPast.length === 0
    },
    noRejectedBookingsFound () {
      return this.bookingsRejected.length === 0
    },
    noRequestedBookingsFound () {
      return this.bookingsRequested.length === 0
    },
    noUpcomingBookingsFound () {
      return this.bookingsUpcoming.length === 0
    }
  },
  watch: {
    async apiMixinInitialised () {
      await this.initialiseBookings()
    }
  },
  beforeDestroy () {
    this.$root.$off('bookingCreated')
    this.$root.$off('bookNow')
  },
  created () {
    this.SEARCH_BY_GYM = Constants.SEARCH_BY_GYM
    this.SEARCH_BY_GYM_ONLINE = Constants.SEARCH_BY_GYM_ONLINE
    this.SEARCH_BY_LOCATION = Constants.SEARCH_BY_LOCATION
  },
  mounted () {
    this.activeTab = this.defaultTab
    this.now = new Date()
    this.$root.$on('bookingCreated', () => {
      if (this.$route.params.professional) {
        this.$router.push({
          name: 'professional-calendar'
        })
      } else {
        this.activeTab = this.defaultTab
      }
    })
    this.$root.$on('bookNow', (locationId, professionalId) => {
      this.locationId = locationId
      this.professionalId = professionalId
      this.activeTab = 0
    })
  },
  methods: {
    async initialiseBookings () {
      this.apiGetNotes()
    },
    onChange (index) {
      if (index === 0) {
        this.$root.$emit('initialiseAddBooking', this.locationId, this.professionalId)
        this.locationId = 0
        this.professionalId = 0
      }
    },
    parsedDateTime (dateTime) {
      return parse(dateTime, 'YYYY-MM-DD HH:mm:ss')
    },
    async refreshImpl () {
      await this.apiGetBookings()
      await this.apiGetNotes()
    }
  }
}
</script>
